import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Grid, CircularProgress, Alert, Table, TableHead, TableRow, TableCell, TableBody, Typography,
} from '@mui/material';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import InvGrnAutocomplete from '../../components/InvGrnAutocomplete';
import { InvTextField, InvSelect } from '../../components/InvField';
import { getGrn, getGrnItems } from '../../api/grn';
import { createVendorReturn } from '../../api/vendorReturn';
import { getVendors } from '../../api/vendor';
import useItemNames from '../../hooks/useItemNames';
import {
  formatVendorReturnError,
  formatVendorReturnLoadError,
  validateVendorReturnForm,
  enrichVendorReturnLinesWithAvailability,
  findVendorReturnStockShortfall,
  isVendorReturnGrnEligible,
} from '../../utils/vendorReturnEntity';
import { generateTxnNumber, formatQty, formatDate } from '../../utils';
import { IPD_COLORS } from '../../theme';

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

const RETURN_TYPES = [
  { value: 'REFUND', label: 'Refund (no replacement expected)' },
  { value: 'REPLACEMENT', label: 'Replacement (vendor will resend)' },
];

function rejectedQtyOf(item) {
  const received = Math.floor(Number(item.received_quantity) || 0);
  const accepted = Math.floor(Number(item.accepted_quantity) || 0);
  if (item.rejected_quantity != null) return Math.floor(Number(item.rejected_quantity) || 0);
  return Math.max(0, received - accepted);
}

export default function VendorReturnForm() {
  const history = useHistory();
  const location = useLocation();
  const { url } = useRouteMatch();
  const base = url.replace(/\/vendor-return\/new$/, '');
  const params = new URLSearchParams(location.search);
  const preselectedGrnId = params.get('grn_id');
  const { resolveItem, resolveItemId } = useItemNames();

  const [loading, setLoading] = useState(!!preselectedGrnId);
  const [saving, setSaving] = useState(false);
  const [posted, setPosted] = useState(false);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const [selectedGrnId, setSelectedGrnId] = useState(preselectedGrnId || '');
  const [grn, setGrn] = useState(null);
  const [vendors, setVendors] = useState([]);

  const [returnNumber] = useState(() => generateTxnNumber('VRET'));
  const [returnType, setReturnType] = useState('REFUND');
  const [returnDate, setReturnDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [lines, setLines] = useState([]);

  useEffect(() => {
    getVendors().then(d => setVendors(normalizeList(d))).catch(() => setVendors([]));
  }, []);

  useEffect(() => {
    if (!selectedGrnId) {
      setGrn(null);
      setLines([]);
      return;
    }
    setLoading(true);
    getGrn(selectedGrnId).then(async (g) => {
      if (!isVendorReturnGrnEligible(g)) {
        toast.error('This GRN is not posted to stock yet. Post the GRN first, then create a vendor return.');
        setGrn(null);
        setLines([]);
        return;
      }
      setGrn(g);
      const scope = { tenant_id: g.tenant_id, hospital_id: g.hospital_id };
      const itemsData = await getGrnItems(selectedGrnId, scope).catch(() => []);
      const rejectable = normalizeList(itemsData)
        .map(item => ({ item, rejectedQty: rejectedQtyOf(item) }))
        .filter(({ rejectedQty }) => rejectedQty > 0)
        .map(({ item, rejectedQty }) => ({
          grn_item_id: item.grn_item_id || item.id,
          item_id: item.item_id,
          item_name: item.item_name || item.item_id,
          uom_id: item.uom_id || '',
          batch_number: item.batch_number || '',
          expiry_date: item.expiry_date || '',
          unit_rate: item.purchase_rate ?? item.unit_rate ?? 0,
          tax_percent: item.tax_percent ?? 0,
          rejectedQty,
          return_quantity: String(rejectedQty),
          reason: item.rejection_reason || '',
        }));
      const enriched = await enrichVendorReturnLinesWithAvailability(
        g.receiving_store_id,
        rejectable,
        resolveItemId,
      );
      setLines(enriched);
      if (!enriched.length) {
        toast.info('This GRN has no rejected quantity to return.');
      }
    }).catch((e) => {
      toast.error(formatVendorReturnLoadError(e, { resolveItemId }));
      setGrn(null);
      setLines([]);
    }).finally(() => setLoading(false));
  }, [selectedGrnId]);

  function resolveVendorName(vendorId) {
    if (!vendorId) return '—';
    const v = vendors.find(x => (x.vendor_id || x.id) === vendorId);
    if (!v) return vendorId;
    return v.vendor_code ? `${v.vendor_name} (${v.vendor_code})` : v.vendor_name;
  }

  function updateLine(index, key, value) {
    setLines(prev => prev.map((line, i) => {
      if (i !== index) return line;
      const next = { ...line, [key]: value };
      if (key === 'return_quantity') {
        const qty = Math.floor(Number(value)) || 0;
        next.stockShort = next.availableQty != null && qty > next.availableQty;
      }
      return next;
    }));
  }

  const totalReturnQty = useMemo(
    () => lines.reduce((sum, l) => sum + (Math.floor(Number(l.return_quantity)) || 0), 0),
    [lines],
  );

  const linesValid = lines.length > 0 && lines.every((l) => {
    const qty = Math.floor(Number(l.return_quantity)) || 0;
    const qtyOk = qty > 0 && qty <= l.rejectedQty;
    const stockOk = l.availableQty == null || qty <= l.availableQty;
    return qtyOk && stockOk;
  });

  async function handleSubmit() {
    setFormError(null);
    setFieldErrors({});

    const validationError = validateVendorReturnForm({
      grn,
      lines,
      returnDate,
      returnNumber,
      resolveItemId,
    });
    if (validationError) {
      if (validationError.field) {
        setFieldErrors({ [validationError.field]: validationError.message });
      } else {
        setFormError(validationError.message);
      }
      toast.error(validationError.message);
      return;
    }

    setSaving(true);
    try {
      const shortfall = await findVendorReturnStockShortfall({
        storeId: grn.receiving_store_id,
        lines,
        resolveItemId,
        actionVerb: 'create',
      });
      if (shortfall) {
        setFormError(shortfall.message);
        toast.error(shortfall.message);
        return;
      }

      const body = {
        tenant_id: grn.tenant_id,
        hospital_id: grn.hospital_id,
        return_number: returnNumber,
        vendor_id: grn.vendor_id,
        source_grn_id: selectedGrnId,
        from_store_id: grn.receiving_store_id,
        return_date: returnDate,
        return_type: returnType,
        reason: reason?.trim() || undefined,
        lines: lines.map(l => ({
          item_id: l.item_id,
          grn_item_id: l.grn_item_id,
          uom_id: l.uom_id || undefined,
          batch_number: l.batch_number || undefined,
          expiry_date: l.expiry_date || undefined,
          return_quantity: Math.floor(Number(l.return_quantity)) || 0,
          unit_rate: Number(l.unit_rate) || 0,
          tax_percent: Number(l.tax_percent) || 0,
          reason: l.reason?.trim() || undefined,
        })),
      };
      await createVendorReturn(body);
      toast.success('Vendor return created');
      setPosted(true);
    } catch (e) {
      const message = formatVendorReturnError(e, { resolveItemId, action: 'save' });
      // Duplicate return_number and "GRN not POSTED_TO_STOCK" are the most common failure
      // modes (409/422) — surface those as a banner the user can act on, not just a toast.
      if (e.status === 409 || e.status === 422) {
        setFormError(message);
      } else {
        toast.error(message);
      }
    } finally {
      setSaving(false);
    }
  }

  if (posted) {
    return (
      <Box>
        <SectionCard title="">
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h5" sx={{ color: IPD_COLORS.textPrimary, fontWeight: 700, mb: 1 }}>
              Vendor Return Created
            </Typography>
            <Typography sx={{ color: IPD_COLORS.textSecondary, mb: 3 }}>
              {returnNumber} has been recorded against GRN {grn?.grn_number}.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
              <InvButton variant="outlined" tone="teal" onClick={() => history.push(`${base}/vendor-returns`)}>
                View All Vendor Returns
              </InvButton>
              <InvButton onClick={() => history.push(`${base}/grn/${selectedGrnId}`)}>
                Back to GRN
              </InvButton>
            </Box>
          </Box>
        </SectionCard>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        eyebrow="Material Management › Vendor Returns"
        title="Create Vendor Return"
        actions={<InvButton variant="outlined" tone="teal" size="small" onClick={() => history.goBack()}>Back</InvButton>}
      />

      {!preselectedGrnId && (
        <SectionCard title="Source GRN" sx={{ mb: 2.5 }}>
          <Box sx={{ maxWidth: 480 }}>
            <InvGrnAutocomplete
              label="Posted GRN"
              value={selectedGrnId}
              onChange={setSelectedGrnId}
              getOptionLabel={(g) => `${g.grn_number} — ${resolveVendorName(g.vendor_id)}`}
            />
          </Box>
        </SectionCard>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: IPD_COLORS.primary }} />
        </Box>
      )}

      {!loading && grn && (
        <>
          <SectionCard title="Return Details" sx={{ mb: 2.5 }}>
            <Grid container spacing={2} sx={{ maxWidth: 960 }}>
              <Grid item xs={12} sm={6} md={4}>
                <InvTextField label="Return Number" value={returnNumber} disabled />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InvTextField label="Source GRN" value={grn.grn_number} disabled />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InvTextField label="Vendor" value={resolveVendorName(grn.vendor_id)} disabled />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InvSelect label="Return Type" value={returnType}
                  onChange={e => setReturnType(e.target.value)} options={RETURN_TYPES} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InvTextField label="Return Date" type="date" value={returnDate}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(fieldErrors.return_date)}
                  helperText={fieldErrors.return_date}
                  onChange={e => setReturnDate(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <InvTextField label="Reason / Remarks" multiline rows={2} value={reason}
                  onChange={e => setReason(e.target.value)} />
              </Grid>
            </Grid>
            {returnType === 'REPLACEMENT' && (
              <Alert severity="info" sx={{ mt: 2, maxWidth: 960 }}>
                Vendor will resend these quantities. This stays open until the replacement arrives —
                receive it as a new GRN against the same purchase order.
              </Alert>
            )}
            {returnType === 'REFUND' && (
              <Alert severity="warning" sx={{ mt: 2, maxWidth: 960 }}>
                No replacement expected — the vendor issues a credit/debit note for this quantity.
                Remember to short-close the purchase order line if nothing further is due.
              </Alert>
            )}
          </SectionCard>

          <SectionCard title="Rejected Line Items">
            {!lines.length ? (
              <Typography sx={{ color: IPD_COLORS.textSecondary, py: 2 }}>
                No rejected quantity found on this GRN.
              </Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Item</TableCell>
                      <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Rejected Qty</TableCell>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 140 }}>Return Qty</TableCell>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Batch</TableCell>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Expiry</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lines.map((line, i) => {
                      const qty = Math.floor(Number(line.return_quantity)) || 0;
                      const invalid = qty <= 0 || qty > line.rejectedQty;
                      const stockShort = line.stockShort || (line.availableQty != null && qty > line.availableQty);
                      const qtyHelper = invalid
                        ? `1–${line.rejectedQty}`
                        : (stockShort ? `Only ${formatQty(line.availableQty)} available in store` : undefined);
                      return (
                        <TableRow key={line.grn_item_id || i}>
                          <TableCell>{resolveItem(line)}</TableCell>
                          <TableCell align="right">{formatQty(line.rejectedQty)}</TableCell>
                          <TableCell sx={{ py: 1 }}>
                            <InvTextField
                              value={line.return_quantity}
                              label=""
                              type="number"
                              inputProps={{ min: 1, max: line.rejectedQty, step: 1 }}
                              error={invalid || stockShort}
                              helperText={qtyHelper}
                              onChange={e => updateLine(i, 'return_quantity', e.target.value)}
                            />
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{line.batch_number || '—'}</TableCell>
                          <TableCell>{formatDate(line.expiry_date)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}

            {formError && (
              <Alert severity="error" sx={{ mt: 2, maxWidth: 960 }} onClose={() => setFormError(null)}>
                {formError}
              </Alert>
            )}

            <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13 }}>
                Total return quantity: <strong style={{ color: IPD_COLORS.textPrimary }}>{formatQty(totalReturnQty)}</strong>
              </Typography>
              <InvButton loading={saving} disabled={!linesValid} onClick={handleSubmit} sx={{ ml: 'auto' }}>
                Create Vendor Return
              </InvButton>
            </Box>
          </SectionCard>
        </>
      )}
    </Box>
  );
}
